import * as Sentry from '@sentry/react';
import { EventHint, SeverityLevel } from '@sentry/react';

import {
  IGNORE_ERRORS_LIST_LOCAL,
  IGNORE_URLS_LIST,
  BLACK_LIST_HANDLED_ERROR_NAMES_LOCAL,
  IError,
  TagsType,
  ExtrasType,
  ISentry,
  ErrorType,
  checkFbBots,
  checkIsOldDevices,
  getCustomTags,
} from '@user-portal/sentry-utils';
import { ENV } from '@user-portal/helpers';

import { getAmazonDefaultData } from './helpers';

import { UserInfo } from './types';

export default class SentryClient {
  sentryDSN: string;
  env: ENV;
  release?: string;
  tracesSampleRate?: number;
  private Initialized: boolean;
  private sentry: typeof Sentry;
  private skipEnvironments: Set<ENV>;

  private static readonly SKIP_INITIALIZATION_ENVIRONMENTS = new Set([ENV.develop]);

  constructor({ sentryDSN, env, release, tracesSampleRate }: ISentry) {
    this.sentryDSN = sentryDSN;
    this.env = env || ENV.stage;
    this.release = release || '1.0.0';
    this.tracesSampleRate = tracesSampleRate || 1.0;
    this.Initialized = false;
    this.sentry = Sentry;
    this.skipEnvironments = SentryClient.SKIP_INITIALIZATION_ENVIRONMENTS;
  }

  private shouldSkipInitialization(envMode?: string): boolean {
    return this.skipEnvironments.has(envMode as ENV);
  }

  private logSystemSkipped(_systemName: string, reason: string): void {
    console.log(`%cSkipping Sentry init (${reason})`, 'color:orange;');
  }

  init() {
    if (this.Initialized) return;

    if (this.shouldSkipInitialization(this.env)) {
      this.logSystemSkipped(this.env, 'nonprod environment');
      return;
    }

    Sentry.init({
      dsn: this.sentryDSN,
      integrations: [Sentry.browserTracingIntegration()],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.5,
      environment: this.env,
      debug: this.env !== ENV.prod,
      release: this.release,
      tracesSampleRate: this.tracesSampleRate,
      maxBreadcrumbs: 10,
      beforeSend: (event: Sentry.ErrorEvent, hint: EventHint) => {
        event.tags = {
          ...event.tags,
          sourceApplication: 'portal',
        };
        return SentryClient.filterEventsBeforeSend(event, hint);
      },
      ignoreErrors: IGNORE_ERRORS_LIST_LOCAL || [],
      denyUrls: IGNORE_URLS_LIST || [],
    });

    this.Initialized = true;
    if (this.Initialized) {
      console.log('Sentry initialized');
    }
  }

  public logError(
    error: IError | unknown,
    type: ErrorType,
    errorLevel: SeverityLevel,
    extras?: ExtrasType,
    customTags?: TagsType
  ) {
    const customTagsArr = customTags || getCustomTags(error, type, extras);

    return SentryClient.getAnalyticData().then((analyticData) =>
      this.sentry.withScope((scope: Sentry.Scope) => {
        extras && scope.setExtras({ ...extras, ...analyticData });

        scope.setTag('ERROR_TYPE', type);
        scope.setTag('ERROR_LEVEL', errorLevel);
        scope.setTag('aws_id', analyticData?.aws_id || null);
        scope.setLevel(errorLevel);

        customTagsArr.length && customTagsArr.forEach(([tag, value]) => scope.setTag(tag, value));

        this.sentry.captureMessage(SentryClient.prepareErrorByCode(error, type));
      })
    );
  }

  public setUser(userData: UserInfo) {
    this.sentry.setUser({ ...userData });
  }

  private static prepareErrorByCode(error: IError | unknown, type: ErrorType) {
    return `[${type}] | : ${JSON.stringify(error)} | ${error}`;
  }

  private static async getAnalyticData() {
    return await getAmazonDefaultData();
  }

  private static filterEventsBeforeSend(event: Sentry.ErrorEvent, hint: EventHint) {
    const error = hint.originalException;

    if (
      (typeof error === 'string' && SentryClient.matchSubstring(error)) ||
      checkIsOldDevices(event) ||
      checkFbBots(window.screen.width, window.screen.height)
    ) {
      return null;
    }

    return event;
  }

  private static matchSubstring(error: string) {
    return BLACK_LIST_HANDLED_ERROR_NAMES_LOCAL.some((item) => error.includes(item));
  }
}
